import { NgIf } from '@angular/common';
import { AfterViewInit, Component, HostListener, OnInit } from '@angular/core';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { forEach } from 'angular';
import { cloneDeep } from 'lodash';
import { NgxMaskService } from 'ngx-mask';
import { forkJoin, of, switchMap } from 'rxjs';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_WRAPPERS, Masks } from '@app/constants';
import { DialogService, FeedbackService, RoleService } from '@app/core/services';
import { UserClaims } from '@app/enums';
import { ColumnFilter, Filter, FilterConfig, TableConfig } from '@app/interfaces';
import { ButtonBarComponent, TitleComponent } from '@app/shared/components';
import { FormComponent, FormMode } from '@app/shared/forms/components';
import { AccountService, AccountStructureService } from '@app/shared/services';
import { SiteService } from '@app/shared/services/site.service';
import { UserService } from '@app/shared/services/user.service';
import { isNil, mapArrayToSelectOptions } from '@app/utils';
import { ReadOnly } from '@app/utils/claims';
import * as Filters from '@app/utils/filters';
import * as Transforms from '@app/utils/transforms';
import { ManageUsersService } from '../manage-users.service';
import { Account } from '../models/account.model';
import { EditUserModel } from '../models/edit-user.model';
import { Site } from '../models/site.model';

enum UserDetailsTab {
	General = 0,
	Sites = 1,
	Accounts = 2,
}

@Component({
	selector: 'sl-user-details',
	standalone: true,
	imports: [FormComponent, TitleComponent, RouterModule, NgIf, ButtonBarComponent],
	providers: [ManageUsersService],
	templateUrl: './details.component.html',
	styleUrl: './details.component.less',
})
export class UserDetailsComponent implements OnInit, AfterViewInit {
	form = new UntypedFormGroup({});

	@HostListener('window:beforeunload')
	canDeactivate = (): boolean => {
		return !this.form.dirty;
	};

	@ReadOnly()
	isReadOnly = true;

	currentSiteSelection: any[] = [];
	currentAccountSelection: any[] = [];

	model = {} as UserSetupModel;
	revertModel = {} as UserSetupModel;
	accountsChanged = false;
	sitesChanged = false;
	hasLoadedSites = false;
	hasLoadedAccounts = false;
	userId: string;
	selectedIndex = 0;
	previousIndex = 0;
	private questionMarkMask = '';
	private readonly defaultAccountFilter = {
		search: '',
		AccountNumber: '',
		Selected: false,
	};
	private readonly defaultSiteFilter = { search: '' };
	private clearSiteSearchTrigger = false;
	private clearAccountSearchTrigger = false;

	secondaryButtonClass = 'linq-button linq-button--secondary';
	primaryButtonClass = 'linq-button linq-button--primary';

	user: EditUserModel = {
		Claims: [],
		Active: false,
		AutoResetType: null,
		Email: '',
		FirstName: '',
		LastName: '',
		FullName: '',
		PhoneNumber: '',
		UserName: '',
		isGA: false,
		GlobalAdminRole: UserClaims.globalAdmin,
		RoleId: '',
		RoleDescription: '',
		HasPassword: false,
		AutoResetFrom: '',
		Sites: [],
		AllSiteAccess: false,
		Accounts: [],
		AllAccountAccess: false,
		EmailConfirmed: false,
		Id: null,
	};

	fields: FormlyFieldConfig[];

	options: FormlyFormOptions = {
		formState: {
			mode: FormMode.Edit,
			readOnly: this.isReadOnly,
			flyout: false,
			selects: {
				roles: [],
				sites: [],
				users: [],
				accountTypes: [],
				roleEntities: [],
				siteEntities: [],
				accountEntities: [],
				initialSiteEntities: [],
				initialAccountEntities: [],
				initialSelectedSiteEntities: [],
				initialSelectedAccountEntities: [],
			},
		},
	};

	siteTableConfig: TableConfig<Site> = {
		defaultSortField: 'SiteName',
		defaultSortDirection: 'asc',
		stickyHeader: true,
		showRowSelect: true,
		selectionField: 'Selected',
		selectionOptions: {
			disabled: () => this.user.AllSiteAccess,
			disabledMessage: () => 'User has access to all sites',
		},
		alwaysShowBulkActionToolbar: true,
		hideBulkActionButtons: true,
		columns: [
			{
				key: 'SiteName',
				header: 'Site Name',
			},
		],
	};

	siteFilterConfig: FilterConfig<any>[] = [];
	siteFilters = { ...this.defaultSiteFilter };

	accountTableConfig: TableConfig<Account> = {
		defaultSortField: 'AccountNumber',
		defaultSortDirection: 'asc',
		stickyHeader: true,
		showRowSelect: true,
		selectionField: 'Selected',
		selectionOptions: {
			disabled: () => this.user.AllAccountAccess,
			disabledMessage: () => 'User has access to all accounts',
		},
		alwaysShowBulkActionToolbar: true,
		hideBulkActionButtons: true,
		columns: [
			{
				key: 'AccountNumber',
				header: 'Account Number',
				filter: ({ row, filterValue }: ColumnFilter<Account>): boolean =>
					filterValue === this.questionMarkMask || Filters.filterAccounts([row], filterValue).length > 0,
			},
			{
				key: 'Description',
				header: 'Description',
			},
			{
				key: 'Selected',
				header: 'Select',
				hidden: true,
				filter: ({ row, filterValue }: ColumnFilter<Account>): boolean => filterValue !== true || row.Selected === true,
			},
		],
	};

	accountFilterConfig: FilterConfig<Account>[] = this.getAccountFilterConfig();
	accountFilterFields: FormlyFieldConfig[] = this.getAccountFilterFields();
	accountFilters = { ...this.defaultAccountFilter };

	constructor(
		private readonly roleService: RoleService,
		private readonly userService: UserService,
		private readonly manageUsersService: ManageUsersService,
		private readonly siteService: SiteService,
		private readonly accountStructureService: AccountStructureService,
		private readonly accountService: AccountService,
		private readonly dialogService: DialogService,
		private readonly feedbackService: FeedbackService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly maskService: NgxMaskService,
	) {
		this.userId = this.route.snapshot.params.id;
	}

	ngOnInit(): void {
		if (this.userId == null) {
			this.router.navigate(['/users']);
		}
		this.fields = this.getFields();
		this.loadDependencies();
	}

	ngAfterViewInit(): void {
		this.getFields();
	}

	loadDependencies(): void {
		const roles$ = this.roleService.getRoleList();
		const users$ = this.userService.list(true);
		const user$ = this.userService.getUser(this.userId);
		const accountStructure$ = this.accountStructureService.getStructure();
		forkJoin([roles$, users$, user$, accountStructure$]).subscribe(([roles, users, user, accountStructure]) => {
			this.options.formState.selects.users = users.users;

			this.options.formState.selects.roles = mapArrayToSelectOptions(roles, {
				label: 'Name',
				value: 'Id',
				all: false,
			});

			this.options.formState.selects.roleEntities = roles;

			this.questionMarkMask = Transforms.toQuestionMask(accountStructure.accountStructure?.UnifiedAccountMask || '');
			this.accountFilters.AccountNumber = this.questionMarkMask;
			this.accountFilterConfig = this.getAccountFilterConfig();
			this.accountFilterFields = this.getAccountFilterFields();

			this.loadUser(user);

			const userTab = this.form.get('tabUser') as FormGroup;
			userTab.get('Email').updateValueAndValidity();
		});
	}

	loadTabData(reload = false): void {
		if (this.selectedIndex === UserDetailsTab.Sites && !this.hasLoadedSites) {
			this.hasLoadedSites = true;
			this.siteService.getList().subscribe((sites) => {
				this.options.formState.selects.siteEntities = sites.sites;
				this.currentSiteSelection = this.user.Sites;
				this.mapSites();
			});
		} else if (this.selectedIndex === UserDetailsTab.Accounts && !this.hasLoadedAccounts) {
			this.hasLoadedAccounts = true;
			forkJoin([
				this.accountService.searchAccounts({
					AccountNumber: '',
					Limit: 1000,
				}),
				this.accountStructureService.getAccountTypes(),
			]).subscribe(([accounts, structures]) => {
				this.options.formState.selects.accountTypes = mapArrayToSelectOptions(structures.accountTypes, {
					label: 'Description',
					value: 'Description',
				});

				this.options.formState.selects.accountEntities = accounts.accounts;
				this.currentAccountSelection = this.user.Accounts;
				this.mapAccounts();
			});
		}

		// reset the form state options to uncheck/recheck options
		if (reload) {
			if (this.previousIndex === UserDetailsTab.Sites) {
				this.options.formState.selects.siteEntities = cloneDeep(this.options.formState.selects.initialSiteEntities);
				this.options.formState.selects.initialSelectedSiteEntities = this.options.formState.selects.siteEntities.filter(
					(site: any) => site.Selected,
				);
				this.user.AllSiteAccess = this.revertModel.tabSites.AllSiteAccess;
				setTimeout(() => {
					this.updateAllSitesSelectedState();
				}, 100);
			} else if (this.previousIndex === UserDetailsTab.Accounts) {
				this.options.formState.selects.accountEntities = cloneDeep(this.options.formState.selects.initialAccountEntities);
				this.options.formState.selects.initialSelectedAccountEntities = this.options.formState.selects.accountEntities.filter(
					(account: any) => account.Selected,
				);
				this.user.AllAccountAccess = this.revertModel.tabAccounts.AllAccountAccess;
				setTimeout(() => {
					this.updateAllAccountsSelectedState();
				}, 100);
			}
		}
	}

	loadUser(user: any): void {
		this.mapUser(user);
		if (user.Role.Name === UserClaims.globalAdmin) {
			this.options.formState.selects.roles.push(UserClaims.globalAdmin);
		}
		this.model = new UserSetupModel(this.user, this.fields);

		this.revertModel = new UserSetupModel(this.user, this.fields);

		this.fields = this.getFields();
	}

	mapUser(user: any): void {
		this.user.AutoResetType = user.user.AutoResetType;
		this.user.Email = user.user.Email;
		this.user.FullName = user.user.FullName;
		this.user.FirstName = user.user.OpenIdUser?.FirstName;
		this.user.LastName = user.user.OpenIdUser?.LastName;
		// break number into phone number format
		this.user.PhoneNumber = this.maskService.applyMask(user.user.PhoneNumber, Masks.Phone);
		this.user.UserName = user.user.UserName;
		this.user.Active = user.user.Active;
		this.user.isGA = user.user.isGA;
		this.user.HasPassword = user.user.hasPassword;
		this.user.AutoResetFrom = user.user.AutoResetFrom;
		this.user.EmailConfirmed = user.user.EmailConfirmed;
		this.user.Id = user.user.Id;
		this.user.OpenIdUser = user.user.OpenIdUser;

		this.mapClaims(user);
		this.mapRole(user);
	}

	mapRole(user: any): void {
		this.user.RoleId = user.Role.Id;
		this.user.RoleDescription = user.Role.Description;
	}

	mapClaims(user: any): void {
		user.Claims.forEach((claim: { Type: string; Value: any }) => {
			if (claim.Type === 'Site') {
				const claimValue = Number(claim.Value);
				if (claimValue === -1) {
					this.user.AllSiteAccess = true;
				} else {
					this.user.Sites.push(claimValue);
				}
			} else if (claim.Type === 'Account') {
				const claimValue = Number(claim.Value);
				if (claimValue === -1) {
					this.user.AllAccountAccess = true;
				} else {
					this.user.Accounts.push(claimValue);
				}
			}
		});
	}

	mapSites(): void {
		const allSiteAccess = this.user.Sites.find((s) => s === UserClaims.allAccess);
		if (allSiteAccess) {
			this.user.AllSiteAccess = true;
		}

		this.options.formState.selects.siteEntities.forEach((site: any) => {
			site.Selected = this.user.AllSiteAccess || !!this.user.Sites.find((s) => s === Number(site.Id));
		});

		this.options.formState.selects.initialSiteEntities = cloneDeep(this.options.formState.selects.siteEntities);
		this.options.formState.selects.initialSelectedSiteEntities = this.options.formState.selects.siteEntities.filter((site: any) => site.Selected);
		this.currentSiteSelection = this.user.Sites;
		setTimeout(() => {
			this.updateAllSitesSelectedState();
		}, 100);
	}

	mapAccounts(): void {
		const allAccountAccess = this.user.Accounts.find((a) => a === UserClaims.allAccess);
		if (allAccountAccess) {
			this.user.AllAccountAccess = true;
		}

		this.options.formState.selects.accountEntities.forEach((account: any) => {
			if (this.user.Accounts.find((a) => a === account.Id) || this.user.AllAccountAccess) {
				account.Selected = true;
			} else {
				account.Selected = false;
			}
		});

		this.options.formState.selects.initialAccountEntities = cloneDeep(this.options.formState.selects.accountEntities);
		this.options.formState.selects.initialSelectedAccountEntities = this.options.formState.selects.accountEntities.filter(
			(account: any) => account.Selected,
		);
		this.currentAccountSelection = this.user.Accounts;
		setTimeout(() => {
			this.updateAllAccountsSelectedState();
		}, 100);
	}

	getFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroupClassName: 'row user-details-form',
				fieldGroup: [
					{
						fieldGroupClassName: '',
						wrappers: [FORMLY_FIELD_WRAPPERS.sectionSingleField],
						props: {
							section: 'mainContainer',
							excludePanelHeader: true,
							class: '',
						},
						fieldGroup: [
							{
								fieldGroup: [
									{
										type: FORMLY_FIELD_TYPES.formlyTabs,
										props: {
											stretchTabs: true,
											selectedIndex: this.selectedIndex,
											change: (_field: FormlyFieldConfig, index: number) => {
												this.handleTabChange(index);
											},
										},
										fieldGroup: [
											{
												key: 'tabUser',
												props: { label: 'General', isTab: true },
												fieldGroup: this.getUserFields(),
											},
											{
												key: 'tabSites',
												props: { label: 'Sites', isTab: true },
												fieldGroup: this.getSiteFields(),
											},
											{
												key: 'tabAccounts',
												props: { label: 'Accounts', isTab: true },
												fieldGroup: this.getAccountFields(),
											},
										],
									},
								],
							},
						],
					},
				],
			},
		];
	}

	getUserFields(): FormlyFieldConfig[] {
		const containerClass = 'container-fluid';
		return [
			{
				fieldGroupClassName: containerClass,
				fieldGroup: [
					{
						key: 'UserName',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-4',
						props: {
							label: 'User Name',
							disabled: true,
						},
					},
					{
						key: 'Email',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-4',
						props: {
							label: 'Email Address',
							disabled: true,
						},
					},
					{
						key: 'DeleteUser',
						wrappers: [],
						type: FORMLY_FIELD_TYPES.button,
						className: 'col-xs-4',
						props: {
							text: 'Delete User',
							buttonClass: this.secondaryButtonClass,
							type: 'button',
							color: 'warn',
							onClick: () => this.deleteUser(),
						},
						expressions: {
							hide: () => this.user.EmailConfirmed,
						},
					},
				],
			},
			{
				fieldGroupClassName: containerClass,
				fieldGroup: [
					{
						key: 'FirstName',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-4',
						props: {
							label: 'First Name',
							required: true,
							change: (field: FormlyFieldConfig) => {
								this.manageUsersService.setFullName(field);
							},
						},
						expressions: {
							'props.disabled': () => this.manageUsersService.isOpenIdUser(this.user),
							'props.required': () => !this.manageUsersService.isOpenIdUser(this.user),
						},
					},
					{
						key: 'LastName',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-4',
						props: {
							label: 'Last Name',
							required: true,
							change: (field: FormlyFieldConfig) => {
								this.manageUsersService.setFullName(field);
							},
						},
						expressions: {
							'props.disabled': () => this.manageUsersService.isOpenIdUser(this.user),
							'props.required': () => !this.manageUsersService.isOpenIdUser(this.user),
						},
					},
					{
						key: 'FullName',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-4',
						props: {
							label: 'Full Name',
							required: true,
						},
					},
				],
			},
			{
				fieldGroupClassName: containerClass,
				fieldGroup: [
					{
						key: 'PhoneNumber',
						type: FORMLY_FIELD_TYPES.maskedInput,
						className: 'col-xs-4',
						props: {
							label: 'Phone Number',
							required: true,
							maxLength: 12,
							minLength: 12,
							maskString: Masks.Phone,
						},
					},
					{
						key: 'Active',
						type: FORMLY_FIELD_TYPES.select,
						className: 'col-xs-4',
						props: {
							label: 'Status',
							required: true,
							options: [
								{ label: 'Active', value: true },
								{ label: 'Inactive', value: false },
							],
						},
					},
				],
			},
			{
				fieldGroupClassName: containerClass,
				fieldGroup: [
					{
						key: 'AutoResetType',
						type: FORMLY_FIELD_TYPES.select,
						className: 'col-xs-4',
						props: {
							label: 'Account Auto-Reset',
							options: [
								{
									value: null,
									label: 'None',
								},
								{
									value: 1,
									label: 'Monthly',
								},
								{
									value: 2,
									label: 'Quarterly',
								},
								{
									value: 3,
									label: 'Every 6 Months',
								},
							],
						},
						expressions: {
							hide: () => this.user.RoleDescription === UserClaims.globalAdmin,
						},
					},
					{
						key: 'AutoResetFrom',
						type: FORMLY_FIELD_TYPES.datepicker,
						className: 'col-xs-4',
						props: {
							label: 'Auto-Reset From',
						},
						expressions: {
							hide: () => this.user.AutoResetType === null,
							'props.required': () => this.user.AutoResetType !== null,
						},
					},
					{
						wrappers: [],
						type: FORMLY_FIELD_TYPES.button,
						key: 'ResetAccount',
						className: 'col-xs-4',
						props: {
							text: 'Reset Account Now',
							buttonClass: this.secondaryButtonClass,
							type: 'button',
							color: 'primary',
							onClick: () => {
								this.sendAccountReset();
							},
						},
					},
				],
			},
			{
				fieldGroupClassName: containerClass,
				fieldGroup: [
					{
						key: 'RoleId',
						type: FORMLY_FIELD_TYPES.select,
						className: 'col-xs-4',
						props: {
							label: 'Role',
							required: true,
							options: [],
							change: (field: any) => {
								const role = this.options.formState.selects.roleEntities.find((r: any) => r.Id === field.formControl.value);
								if (role) {
									field.formControl.parent.get('RoleDescription').setValue(role.Description);
								}
							},
						},
						expressions: {
							'props.options': () => this.options.formState.selects.roles,
							'props.required': () => this.user.RoleDescription !== UserClaims.globalAdmin,
							hide: () => this.user.RoleDescription === UserClaims.globalAdmin,
						},
					},
					{
						key: 'GlobalAdminRole',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-4',
						props: {
							label: 'Role',
							disabled: true,
						},
						expressions: {
							hide: () => this.user.RoleDescription !== UserClaims.globalAdmin,
						},
					},
					{
						key: 'RoleDescription',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-4',
						props: {
							label: 'Role Description',
							disabled: true,
						},
					},
				],
			},
		] as FormlyFieldConfig[];
	}

	private getAccountFilterConfig(): FilterConfig<Account>[] {
		return [
			{
				key: 'AccountNumber',
				label: 'Account Mask',
				clearedValue: this.questionMarkMask,
				transform: (fcFilter: Filter): string => (fcFilter.value === this.questionMarkMask ? null : fcFilter.value),
			},
			{
				key: 'AccountType',
				clearedValue: undefined,
			},
			{
				key: 'Selected',
				label: 'Only Selected Accounts',
				clearedValue: false,
				transform: (fcFilter: Filter): string => (fcFilter.value ? 'Selected Only' : null),
			},
		] as FilterConfig<Account>[];
	}

	private getAccountFilterFields(): FormlyFieldConfig[] {
		return [
			{
				key: 'AccountNumber',
				className: 'col-xs-12',
				type: FORMLY_FIELD_TYPES.maskedInput,
				props: {
					label: 'Account Mask',
					showMaskTyped: false,
					dropSpecialCharacters: false,
					questionMarkMask: this.questionMarkMask,
					maxLength: this.questionMarkMask.length,
				},
			},
			{
				key: 'AccountType',
				className: 'col-xs-12',
				type: FORMLY_FIELD_TYPES.select,
				props: {
					label: 'Account Type',
					options: [],
					placeholder: 'Select Account Type',
				},
				expressions: {
					'props.options': () => this.options.formState.selects.accountTypes,
				},
			},
			{
				key: 'Selected',
				className: 'col-xs-12 mat-checkbox-override-infix',
				type: FORMLY_FIELD_TYPES.checkbox,
				props: {
					label: 'Only Selected Accounts',
					appearance: 'fill',
				},
			},
		] as FormlyFieldConfig[];
	}

	getAccountFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroup: [
					{
						key: 'AllAccountAccess',
						type: FORMLY_FIELD_TYPES.checkbox,
						className: 'col-xs-3 sl-form--checkbox',
						props: {
							label: 'Select All Accounts',
							appearance: 'fill',
							change: (field: FormlyFieldConfig) => {
								if (field.formControl.value) {
									this.accountFilters = { ...this.defaultAccountFilter };
								}
								this.user.AllAccountAccess = field.formControl.value;
							},
						},
					},
					{
						key: 'Accounts',
						type: FORMLY_FIELD_TYPES.formlyTable,
						className: 'col-xs-12',
						props: {
							noDataText: 'No results found.',
							data: [],
							activeFilter: true,
							config: [],
							searchOnly: false,
						},
						expressions: {
							'props.config': () => this.accountTableConfig,
							'props.data': () => this.options.formState.selects.accountEntities,
							'props.initialSelectedData': () => this.options.formState.selects.initialSelectedAccountEntities,
							'props.selectionSelectAll': () => this.user.AllAccountAccess,
							'props.disableFilters': () => this.user.AllAccountAccess,
							'props.clearSearchFilter': () => this.user.AllAccountAccess || this.clearAccountSearchTrigger,
							'props.filtersConfig': () => this.accountFilterConfig,
							'props.filters': () => this.accountFilters,
							'props.filterFields': () => this.accountFilterFields,
						},
					},
				],
			},
		];
	}

	getSiteFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroup: [
					{
						key: 'AllSiteAccess',
						type: FORMLY_FIELD_TYPES.checkbox,
						className: 'col-xs-3 sl-form--checkbox',
						props: {
							label: 'Select All Sites',
							appearance: 'fill',
							change: (field: FormlyFieldConfig) => {
								if (field.formControl.value) {
									this.siteFilters = { ...this.defaultSiteFilter };
								}
								this.user.AllSiteAccess = field.formControl.value;
							},
						},
					},
					{
						className: 'col-xs-12',
						key: 'Sites',
						type: FORMLY_FIELD_TYPES.formlyTable,
						props: {
							noDataText: 'No results found.',
							data: [],
							activeFilter: true,
							config: [],
							searchOnly: true,
						},
						expressions: {
							'props.config': () => this.siteTableConfig,
							'props.data': () => this.options.formState.selects.siteEntities,
							'props.initialSelectedData': () => this.options.formState.selects.initialSelectedSiteEntities,
							'props.selectionSelectAll': () => this.user.AllSiteAccess,
							'props.disableFilters': () => this.user.AllSiteAccess,
							'props.clearSearchFilter': () => this.user.AllSiteAccess || this.clearSiteSearchTrigger,
							'props.filtersConfig': () => this.siteFilterConfig,
							'props.filters': () => this.siteFilters,
							'props.model': () => this.model.tabSites,
						},
					},
				],
			},
		] as FormlyFieldConfig[];
	}

	handleTabChange(targetIndex: number): void {
		if (this.formHasChanges) {
			this.dialogService
				.open({
					message: 'You have unsaved changes. Are you sure you want to switch tabs?',
					showCancel: true,
				})
				.subscribe((result) => {
					if (result) {
						this.selectedIndex = targetIndex;
						this.loadTabData(true);
						this.previousIndex = targetIndex;
						this.model = new UserSetupModel(this.user, this.fields);
						this.form.markAsPristine();
					} else {
						this.selectedIndex = this.previousIndex;
						this.fields = this.getFields();
					}
				});
		} else {
			this.selectedIndex = targetIndex;
			this.previousIndex = targetIndex;
			this.loadTabData();
		}
	}

	get formHasChanges(): boolean {
		if (this.isReadOnly) {
			return false;
		}

		return this.form.dirty || this.getChangedSites().length > 0 || this.getChangedAccounts().length > 0;
	}

	get formIsInvalid(): boolean {
		if (this.isReadOnly) {
			return false;
		}

		if (this.selectedIndex === UserDetailsTab.General) {
			return this.userDetailsFormInValid();
		}

		return false;
	}

	userDetailsFormInValid(): boolean {
		const userTab = this.form.get('tabUser') as FormGroup;
		const controls = userTab.controls;

		if (!this.form.dirty) {
			return false;
		}

		for (const name in controls) {
			const control = controls[name];

			if (control.invalid) {
				return true;
			}
		}

		return false;
	}

	cancelChanges(): void {
		if (this.formHasChanges) {
			this.dialogService
				.open({
					message: 'You have unsaved changes. Are you sure you want to discard them?',
					showCancel: true,
				})
				.subscribe((result) => {
					if (result) {
						this.clearSearch();
						this.loadTabData(true);
						this.model = new UserSetupModel(this.user, this.fields);
						this.form.markAsPristine();
					}
				});
		}
	}

	clearSearch(): void {
		if (this.selectedIndex === UserDetailsTab.Sites) {
			this.siteFilters = { ...this.defaultSiteFilter };
			this.clearSiteSearchTrigger = true;
			// Reset the trigger after Angular processes the change detection cycle
			setTimeout(() => (this.clearSiteSearchTrigger = false), 0);
		} else if (this.selectedIndex === UserDetailsTab.Accounts) {
			this.accountFilters = { ...this.defaultAccountFilter };
			this.clearAccountSearchTrigger = true;
			// Reset the trigger after Angular processes the change detection cycle
			setTimeout(() => (this.clearAccountSearchTrigger = false), 0);
		}
	}

	saveChanges(): void {
		if (this.selectedIndex === UserDetailsTab.General) {
			this.saveUser();
		} else if (this.selectedIndex === UserDetailsTab.Sites) {
			this.saveSites();
		} else if (this.selectedIndex === UserDetailsTab.Accounts) {
			this.saveAccounts();
		}
	}

	saveUser(): void {
		const model = this.model.flattenModel();
		model.Id ??= this.userId;
		// removing dashes from phone number
		model.PhoneNumber = model.PhoneNumber.replace(/-/g, '');
		this.saveUserWithoutEmailChange(model);
	}

	saveUserWithoutEmailChange(model: any): void {
		this.userService.update(model).subscribe((result) => {
			if (result.isValid) {
				this.user = { ...this.user, ...model };
				this.revertModel = new UserSetupModel(this.user, this.fields);
				this.fields = this.getFields();
				this.form.markAsPristine();
				this.feedbackService.success('updated', 'User');
			} else {
				const errors = result.errors.join(' ');
				this.feedbackService.error(errors);
			}
		});
	}

	deleteUser(): void {
		this.dialogService
			.open({
				title: 'Delete User?',
				message: 'User will be permanently deleted.',
				showCancel: true,
				okText: 'DELETE',
				okColor: 'warn',
			})
			.pipe(
				switchMap((result) => {
					if (result) {
						return this.userService.deleteEntity(this.userId);
					}
					return of(null);
				}),
			)
			.subscribe((result) => {
				if (result == null) {
					return;
				}

				if (!result.isValid) {
					const errors = result.errors.join(' ');
					this.feedbackService.error(errors);
				} else {
					this.router.navigate(['lsb/systemSettings/users']);
				}
			});
	}

	saveSites(): void {
		const model = this.model.flattenModel();
		let claims = [];
		if (model.AllSiteAccess) {
			claims = [UserClaims.allAccess];
		} else {
			claims = this.options.formState.selects.siteEntities.filter((a: any) => a.Selected).map((a: any) => a.Id) as string[];
		}

		this.user.Sites = claims;
		this.user.Claims = [...this.user.Claims, ...claims];

		this.userService.updateUserClaims(this.userId, 'Site', claims).subscribe((result) => {
			if (!result.isValid) {
				const errors = result.errors.join(' ');
				this.feedbackService.error(errors);
			} else {
				this.model = new UserSetupModel(this.user, this.fields);
				this.revertModel = new UserSetupModel(this.user, this.fields);
				this.mapSites();
				this.form.markAsPristine();
				this.feedbackService.success('updated', 'User');
			}
		});
	}

	saveAccounts(): void {
		const model = this.model.flattenModel();
		let claims = [];
		if (model.AllAccountAccess) {
			claims = [UserClaims.allAccess];
		} else {
			claims = this.options.formState.selects.accountEntities.filter((s: any) => s.Selected).map((a: any) => a.Id) as string[];
		}

		this.user.Accounts = claims;
		this.user.Claims = [...this.user.Claims, ...claims];

		this.userService.updateUserClaims(this.userId, 'Account', claims).subscribe((result) => {
			if (!result.isValid) {
				const errors = result.errors.join(' ');
				this.feedbackService.error(errors);
			} else {
				this.model = new UserSetupModel(this.user, this.fields);
				this.revertModel = new UserSetupModel(this.user, this.fields);
				this.mapAccounts();
				this.form.markAsPristine();
				this.feedbackService.success('updated', 'User');
			}
		});
	}

	sendAccountReset(): void {
		this.dialogService
			.open({
				title: 'Reset Account?',
				message: 'User will be sent a new activation link and will have to reverify their identity and reset their password.',
				showCancel: true,
				okText: 'RESET',
				okColor: 'warn',
			})
			.pipe(
				switchMap((result) => {
					if (result) {
						return this.userService.resetUser(this.user.Email);
					}
					return of(null);
				}),
			)
			.subscribe((result) => {
				if (result == null) {
					return;
				}

				if (!result?.isValid) {
					this.feedbackService.error('Unable to reset user account. Please contact your administrator.');
				} else {
					this.feedbackService.customSuccess('Account reset successfully.');
				}
			});
	}

	getChangedAccounts(): number[] {
		const initialAccounts = this.options.formState.selects.initialAccountEntities;
		const currentAccounts = this.options.formState.selects.accountEntities;

		const checkedAccounts = currentAccounts
			.filter((current: any) => current.Selected && !initialAccounts.some((initial: any) => initial.Id === current.Id && initial.Selected))
			.map((a: any) => a.Id);

		const uncheckedAccounts = initialAccounts
			.filter((initial: any) => initial.Selected && !currentAccounts.some((current: any) => current.Id === initial.Id && current.Selected))
			.map((a: any) => a.Id);

		return [...checkedAccounts, ...uncheckedAccounts];
	}

	getChangedSites(): number[] {
		const initialSites = this.options.formState.selects.initialSiteEntities;
		const currentSites = this.options.formState.selects.siteEntities;

		const checkedSites = currentSites
			.filter((current: any) => current.Selected && !initialSites.some((initial: any) => initial.Id === current.Id && initial.Selected))
			.map((a: any) => a.Id);

		const uncheckedSites = initialSites
			.filter((initial: any) => initial.Selected && !currentSites.some((current: any) => current.Id === initial.Id && current.Selected))
			.map((a: any) => a.Id);

		return [...checkedSites, ...uncheckedSites];
	}

	updateAllSitesSelectedState(): void {
		const allSitesSelected = this.options.formState.selects.siteEntities.every((site: any) => site.Selected);
		const allSitesControl = this.form.get('tabSites.AllSiteAccess');
		if (allSitesControl && this.model.tabSites.AllSiteAccess) {
			allSitesControl.setValue(allSitesSelected, { emitEvent: false });
		}
	}

	updateAllAccountsSelectedState(): void {
		const allAccountsSelected = this.options.formState.selects.accountEntities.every((account: any) => account.Selected);
		const allAccountsControl = this.form.get('tabAccounts.AllAccountAccess');
		if (allAccountsControl && this.model.tabAccounts.AllAccountAccess) {
			allAccountsControl.setValue(allAccountsSelected, { emitEvent: false });
		}
	}
}

class UserSetupModel {
	tabUser: any;
	tabSites: any;
	tabAccounts: any;

	constructor(user: any, fields: FormlyFieldConfig[]) {
		const keyArray: any[] = [];

		this.evaluateFields(fields, false, keyArray);
		this.loadModel(user, keyArray);
	}

	public flattenModel(): any {
		const flatObject: Record<string, any> = {};

		const flatten = (data: any) => {
			Object.keys(data).forEach((key) => {
				const value = data[key];

				if (typeof value === 'object' && !Array.isArray(value)) {
					Object.assign(flatObject, value);
				} else {
					flatObject[key] = value;
				}
			});
		};

		flatten(this);

		return flatObject;
	}

	private evaluateFields(fields: FormlyFieldConfig[], isNested = false, keyArray: any[] = []): void {
		fields.forEach((field) => {
			const arrayCount = keyArray.length;
			if (!isNested) {
				keyArray.push({ [arrayCount]: [] });
			}

			if (field.key || field.type) {
				keyArray[keyArray.length - 1][keyArray.length - 1].push({ key: field.key, type: field.type, isTab: field.props?.isTab || false });
			}

			if (field.fieldGroup) {
				this.evaluateFields(field.fieldGroup, true, keyArray);
			}
		});
	}

	private loadModel(data: any, keyArray: any[]): void {
		keyArray.forEach((fieldsArray) => {
			const internalArray = fieldsArray[0];
			let tabName: 'tabUser' | 'tabSites' | 'tabAccounts' | null = null;
			forEach(internalArray, (item) => {
				if (item.isTab) {
					tabName = item.key as 'tabUser' | 'tabSites' | 'tabAccounts';
					this[tabName] = {};
				}

				if (!item.isTab && tabName !== null) {
					if (!isNil(data[item.key])) {
						this[tabName][item.key] = data[item.key];
					}
				}
			});
		});
	}
}
